import Button from '~/src/common/components/Button';
import PickerDebounced from '~/src/common/components/PickerDebounced';
import PromoPrice from '~/src/common/components/PromoPrice';
import { useNotifyNthPromo } from '~/src/common/hooks/cart/useNotifyNthPromo';
import {
  UpdateCartProductQuantityPayload,
  useUpdateCartProductQuantity,
} from '~/src/common/hooks/cart/useUpdateCartProductQuantity';
import { useDelivery } from '~/src/common/hooks/useDelivery';
import { useStable } from '~/src/common/hooks/useStable';
import { useHandleFirstAddToCartError } from '~/src/common/services/error-handler/error-handler';
import I18n from '~/src/common/services/I18n';
import { useNiceModal } from '~/src/common/services/ModalsManager';
import Tracker, { getCartEventMapping } from '~/src/common/services/Tracker';
import { HomeProduct, ProductDetailed } from '~/src/common/typings/product';
import { getRestockDate } from '~/src/common/utils/date';
import { formatPrice } from '~/src/common/utils/prices';
import { isSameProduct } from '~/src/common/utils/product';
import { useGetCart } from '~/src/queries/api-ecom/generated/api-ecom';
import { GetDetailBySlug200BreadcrumbsItem } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import useCatalogContentSource from '~/src/screens/CatalogContent/ContentScreen/hooks/useCatalogContentSource';

import * as S from './layout';

interface Props {
  product: HomeProduct | ProductDetailed;
  freeProductQuantity: number;
  categoriesBreadcrumbs: Partial<GetDetailBySlug200BreadcrumbsItem>[];
  onRefresh: () => void;
}

const AddToCartAction = ({
  product,
  freeProductQuantity,
  categoriesBreadcrumbs,
  onRefresh,
}: Props) => {
  const { id, availableQuantity, itemPrice, promo, restockDate, packSize, shortage } = product;

  const { data: cart } = useGetCart();
  const deliveryAddressModal = useNiceModal('delivery-address-modal');
  const deliverySelectSlotModal = useNiceModal('delivery-select-slot-modal');

  const productQuantity =
    cart?.products?.find(p => isSameProduct(p, product))?.quotation2.count.quantity ?? 0;

  const isEnabledOrUnknown = product && 'enabled' in product ? product.enabled : true;
  const hasQuantity = product && product.availableQuantity > 0;
  const isProductAvailable = isEnabledOrUnknown && hasQuantity;
  const delivery = useDelivery();
  const categoryName = categoriesBreadcrumbs[0]?.name;
  // Cas spécifique pour les produits qui sont dans une sous-catégorie de la home
  // Les infos sont dans le 1er élément du tableau car pas de catégories parentes
  const subcategory = categoriesBreadcrumbs[1] ?? categoriesBreadcrumbs[0];
  const hasValidTimeSlot = delivery.isSetup && delivery.isTimeSlotValid;
  const hasPromo = promo != null;
  const isBatchDiscount = hasPromo && promo.mechanism === 'BATCH_DISCOUNT';
  const isPromoDisplayed = hasPromo && !isBatchDiscount;

  const handleNotifyNthPromo = useNotifyNthPromo(product);

  const source = useCatalogContentSource({
    type: 'main product',
    detail: {},
  });

  const updateProductQuantityParams: UpdateCartProductQuantityPayload = {
    source,
    product,
    subcategoryName: subcategory?.name,
    categoryName,
    onRefresh,
    onSuccess: handleNotifyNthPromo,
  };

  const { mutate: updateQuantity } = useUpdateCartProductQuantity(updateProductQuantityParams);

  const unavailableLabel =
    restockDate != null
      ? `${I18n.t('view-product-screen.be-back')} ${getRestockDate(
          restockDate.date,
          restockDate.displayHour,
        )}`
      : shortage;

  const handleQuantityChange = useStable((quantity: number) => {
    updateQuantity({
      data: { product: { id, quantity, categoryId: subcategory?.id } },
    });
  });

  const handleDisabledPickerClick = useStable(async (quantity: number, increment: number) => {
    Tracker.sendEvent(
      'click add to cart',
      getCartEventMapping({
        product,
        cart,
        categoryName,
        subcategoryName: subcategory?.name,
        quantity: 1,
        source,
        status: delivery.address ? 'need slot' : 'need shipping',
      }),
    );

    if (delivery.address) {
      await deliverySelectSlotModal.show();
    } else {
      await deliveryAddressModal.show();
    }

    updateQuantity(
      {
        data: { product: { id, quantity: quantity + increment, categoryId: subcategory?.id } },
      },
      { onError: useHandleFirstAddToCartError },
    );
  });

  if (!isProductAvailable) {
    return (
      <S.AddToCartActionContainer>
        <S.UnavailableContainer>
          {restockDate != null ? (
            <S.Unavailable>{I18n.t('view-product-screen.unavailable')}</S.Unavailable>
          ) : null}
          <S.Unavailable>{unavailableLabel}</S.Unavailable>
        </S.UnavailableContainer>
      </S.AddToCartActionContainer>
    );
  }

  return (
    <S.AddToCartActionContainer>
      {isPromoDisplayed ? (
        <PromoPrice
          promoPrice={formatPrice(itemPrice * packSize)}
          originalPrice={
            promo.itemOriginalPrice ? formatPrice(promo.itemOriginalPrice * packSize) : ''
          }
          inline={false}
        />
      ) : (
        <S.Price>
          {formatPrice(
            isBatchDiscount && promo?.itemOriginalPrice
              ? promo.itemOriginalPrice * packSize
              : itemPrice * packSize,
          )}
        </S.Price>
      )}
      <S.ButtonContainer>
        <PickerDebounced
          availableQuantity={availableQuantity}
          onQuantityChange={handleQuantityChange}
          enabled={hasValidTimeSlot}
          onDisabledClick={(quantity, increment) =>
            void handleDisabledPickerClick(quantity, increment)
          }
          cachedQuantity={productQuantity}
          packSize={packSize}
          emptyComponent={
            <Button
              icon="basket-normal"
              aria-label={I18n.t('view-product-screen.add-to-cart')}
              disabled={!(availableQuantity === 0) && freeProductQuantity > 0}>
              {I18n.t('view-product-screen.add-to-cart-with-icon')}
            </Button>
          }
          size="large">
          <S.PickerQuantity />
        </PickerDebounced>
      </S.ButtonContainer>
    </S.AddToCartActionContainer>
  );
};

export default AddToCartAction;
